import isEmpty from "is-empty";

import {
    HUMANIZED_OBJECTIVES,
    OBJECTIVES,
} from "src/redux/Tx/constants";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";
import PrefixedInput from "src/components/common/AgGrid/PrefixedInput";

import styles from "src/styles/agGridCustom.module.scss";

export const EXT_CPTY_PREFIX = "EXT-";

export const isEveryElementUnique = (arr) => {
    return new Set(arr).size === arr.length;
};

export const isCptyCodePrefixed = (cptyCode) => {
    return (
        cptyCode.length > EXT_CPTY_PREFIX.length &&
        cptyCode.startsWith(EXT_CPTY_PREFIX)
    );
};

export const isCptyCodeValid = (cptyCode) => {
    return (
        !isEmpty(cptyCode) &&
        isCptyCodePrefixed(cptyCode) &&
        cptyCode.length <= 15 &&
        cptyCode.match(/^[A-Z0-9-]+$/g)
    );
};

export const isCptyNameValid = (cptyName) => {
    return !isEmpty(cptyName) && cptyName.length <= 50;
};

const hasCellValueChanged = (params) =>
    params.data.isNew ||
    JSON.stringify(params.value ?? "") !==
        JSON.stringify(params.data.originValue[params.colDef.field] ?? "");

export const defaultColDef = {
    resizable: false,
    sortable: false,
    filter: false,
    headerClass: styles.agHeaderCenterAlign,
    tooltipComponent: "txTooltip",
    menuTabs: [],
};
export const peerCptyColumnDefs = (allowCreateSecret) => {
    let headers = [
        {
            headerName: "Code",
            colId: "Details Code",
            field: "cptyCode",
            width: 110,
            editable: (params) => !!params.data.isNew,
            cellClassRules: {
                [styles.agConfirmEditable]: (params) =>
                    params.colDef.editable(params),
                [styles.agConfirmModified]: hasCellValueChanged,
                [styles.agConfirmCellError]: (params) =>
                    params.colDef.editable(params) &&
                    (!isCptyCodeValid(params.value) ||
                        !isEveryElementUnique(
                            params.context.allSettings.map((s) => s.cptyCode)
                        )),
            },
            cellEditor: PrefixedInput,
            cellEditorParams: {
                prefix: EXT_CPTY_PREFIX,
            },
            tooltipValueGetter: (params) => {
                return { value: params.value };
            },
            tooltipComponentParams: (params) => {
                if (params.value.value.length >= 10) {
                    return { type: "counterpartyCode", value: params.value };
                } else {
                    return undefined;
                }
            },
        },
        {
            headerName: "Name",
            colId: "Details Name",
            field: "cptyName",
            flex: 2,
            editable: (params) => !!params.data.isNew,
            cellClass: styles.agLeftAlign,
            cellClassRules: {
                [styles.agConfirmEditable]: (params) =>
                    params.colDef.editable(params),
                [styles.agConfirmModified]: hasCellValueChanged,
                [styles.agNewValueCellError]: (params) =>
                    params.colDef.editable(params) &&
                    (!isCptyNameValid(params.value) ||
                        !isEveryElementUnique(
                            params.context.allSettings.map((s) => s.cptyName)
                        )),
            },
        },
        {
            headerName: "Default Objective",
            colId: "Details Default Objective",
            field: "defaultObjective",
            width: 170,
            editable: true,
            valueFormatter: (params) =>
                HUMANIZED_OBJECTIVES[params.value] || params.value || "-",
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                values: [
                    "",
                    OBJECTIVES.REGULAR,
                    OBJECTIVES.LP,
                    OBJECTIVES.FINANCING,
                ],
                formatValue: (value) => HUMANIZED_OBJECTIVES[value] || "-",
                cellRenderer: DropdownItemRenderer,
            },
            cellClass: [styles.agLeftAlign, styles.agConfirmEditable],
            cellClassRules: {
                [styles.agConfirmModified]: hasCellValueChanged,
            },
        },
        {
            headerName: "E-mail Address",
            field: "emails",
            colId: "Details Emails",
            flex: 3,
            editable: (params) => params.data.isUnregisteredCpty,
            cellClassRules: {
                [styles.agConfirmEditable]: (params) =>
                    params.colDef.editable(params),
                [styles.agNewValueCellError]: (params) =>
                    params.colDef.editable(params) && isEmpty(params.value),
                [styles.agConfirmModified]: hasCellValueChanged,
            },
            cellStyle: { overflowY: "auto", overflowX: "hidden" },
            cellRenderer: "multiEmailsRenderer",
            cellEditor: "multiEmailsEditor",
            cellEditorPopup: true,
        },
        {
            headerName: "Delete",
            field: "delete",
            colId: "Details Delete",
            width: 60,
            cellRenderer: "deleteButton",
        },
    ];

    if (allowCreateSecret) {
        headers.splice(headers.length - 1, 0, {
            headerName: "Secret Key",
            field: "secret",
            colId: "Details Secret",
            flex: 3,
            editable: false,
            cellClass: styles.agCenterAlign,
            cellStyle: {
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
            cellRenderer: "secretButton",
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: (params) =>
                params.data.isUnregisteredCpty
                    ? { type: "secretButtonTooltip" }
                    : null,
        });
    }

    return headers;
};
